import "./styles/stack.scss";

import { useSpring, animated } from "@react-spring/web";
import { api } from "@src/utils/axios-instance";

import { XMarkIcon } from "@heroicons/react/24/solid";
import CharacterCard from "../CharacterCard/CharacterCard";
import SearchInput from "../SearchInput/SearchInput";

import { useEffect, useRef, useState } from "react";
import { getIndex } from "@src/utils/array";
import { useCharacterStore } from "@src/stores/characterStore";
import { getDefaultVersion } from "@src/utils/character-utils";
import { useQuery } from "@tanstack/react-query";
import { Character } from "@src/types/global";
import { useUserStore } from "@src/stores/userStore";
import Input from "../Input/Input";

type Item = {
    id: string;
    name: string;
};
enum Filters {
    OWN = "own",
    ALL = "all",
    SHARED = "granted",
}
const Stack = () => {
    const {
        addVersionToList,
        characterList,
        removeCharacterVersion,
        setCharacterList,
    } = useCharacterStore((state) => state);
    const { id } = useUserStore();
    const [open, setOpen] = useState(false);
    const [filters, setFilters] = useState<Filters>(Filters.OWN);
    const prevFilters = useRef(Filters.OWN);
    const [searchResults, setSearchResults] = useState<Character[] | []>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [displayResults, setDisplayResults] = useState<
        { name: string; id: string }[] | []
    >([]);
    const [isDragged, setIsDragged] = useState<boolean>(false);
    const [draggedCharacter, setDraggedCharacter] = useState<number | null>(
        null,
    );
    const [insertIndex, setInsertIndex] = useState<number | null>(null);
    const [ghostPosition, setGhostPosition] = useState({ y: "50%" });

    const search = useRef("");
    async function fetchByName(
        name: string,
    ): Promise<{ accessible_characters: Character[] }> {
        const { data } = await api.get(
            "characters/?name=" + name + "&type=" + filters,
        );
        return data.data;
    }
    /**
     * Transforms the array of given characters to an array of {name: string, id: string} objects
     * for display in the searchResults
     * @param {Character[]} characters - The array of Characters coming from the server to convert
     */
    function transformSearchResults(characters: Character[]) {
        const charactersArray = characters
            .filter((character) => {
                if (filters === Filters.OWN) {
                    return character.owner_id == id;
                }
                if (filters === Filters.SHARED) {
                    return true;
                }
            })
            .map((character) => {
                return {
                    name: character.last_name + " " + character.first_name,
                    id: character.id,
                };
            });
        setDisplayResults(charactersArray);
    }

    const { refetch, data, isSuccess, isRefetching } = useQuery({
        queryKey: ["name", search.current, filters],
        queryFn: () => fetchByName(search.current),
        staleTime: 5000,
        enabled: Boolean(search.current),
    });

    function updateResults() {
        setSearchResults(data?.accessible_characters || []);
        transformSearchResults(data?.accessible_characters || []);
    }
    useEffect(() => {
        updateResults();
        if (isRefetching) {
            setSearchLoading(true);
        } else {
            setSearchLoading(false);
        }
    }, [isSuccess, isRefetching]);

    /**
     * The function to call whenever a user types in the box
     * @param value - The user input
     */
    const handleChange = async (value: string) => {
        if (value !== "") {
            updateResults();
            search.current = value;
        } else {
            setDisplayResults([]);
            setSearchResults([]);
            search.current = "";
        }
    };

    const handleClick = (item: Item) => {
        const index = getIndex(item.id, searchResults, "id");
        const characterWithVersion = getDefaultVersion(searchResults[index]);
        addVersionToList(characterWithVersion);
    };
    const [filtersStyle, filterSpringApi] = useSpring(
        () => ({
            from: { opacity: 0, height: "0" },
        }),
        [],
    );
    const showFilters = (shouldShow: boolean) => {
        filterSpringApi.start({
            to: { opacity: 1, height: "auto" },
            reverse: !shouldShow,
        });
    };

    const handleFilters = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value: checked } = e.target || {};
        const filter = checked ? Filters.SHARED : Filters.OWN;
        setFilters(filter);
    };
    if (prevFilters?.current !== filters) {
        refetch();
        prevFilters.current = filters;
    }
    const removeCharacterFromStack = (id: string) => {
        removeCharacterVersion(id);
    };
    const [props, springApi] = useSpring(
        () => ({
            from: { opacity: 0, height: 0 },
        }),
        [],
    );
    function handleHover(e: MouseEvent, index: number) {
        const mouseY = e.clientY;
        const currentTarget = e.currentTarget as HTMLDivElement;
        const targetMaxY = currentTarget.getBoundingClientRect().y;
        const targetMinY = targetMaxY + currentTarget.clientHeight;
        const halfwayPoint = targetMaxY + currentTarget.clientHeight / 2;
        let swapIndex = index;

        if (mouseY < targetMinY && mouseY > halfwayPoint) {
            setInsertIndex(index + 1);
        }
        if (mouseY > targetMaxY && mouseY < halfwayPoint) {
            setInsertIndex(swapIndex - 1);
        }
    }
    function handleDrop() {
        if (draggedCharacter !== null && insertIndex !== null) {
            const charToInsert = characterList[draggedCharacter];
            let newCharacterList = characterList.filter(
                (_, i) => i !== draggedCharacter,
            );
            // NOTE: An insertIndex of 0 will take the spot of the 1st character, that should be reserved only for an insertIndex of -1
            if (insertIndex === -1) {
                newCharacterList = [charToInsert, ...newCharacterList];
            } else if (insertIndex === 0) {
                newCharacterList.splice(1, 0, charToInsert);
            } else {
                newCharacterList.splice(insertIndex, 0, charToInsert);
            }
            setCharacterList(newCharacterList);
        }
    }
    const ghostCard = useRef<HTMLSpanElement>(null);
    let dragTimeout;
    return (
        <>
            <div
                className="stack"
                style={{ userSelect: isDragged ? "none" : "initial" }}
            >
                <animated.div
                    style={{ opacity: props.opacity }}
                    className={`stack__container ${open ? "is-open" : ""}`}
                >
                    <animated.div
                        style={{ ...filtersStyle, marginBottom: "10px" }}
                        className="filters"
                    >
                        <Input
                            type="checkbox"
                            name="filter"
                            label="Show shared characters"
                            register={() => ({
                                name: "filter",
                                ref: () => {},
                                onChange: handleFilters,
                            })}
                        />
                    </animated.div>
                    <SearchInput
                        placeholder="Enter a name here..."
                        name="character-name"
                        searchResults={displayResults}
                        onChange={handleChange}
                        isLoading={searchLoading}
                        onClick={handleClick}
                        onFocus={() => showFilters(true)}
                        onClickOutside={() => setDisplayResults([])}
                    />
                    <animated.div
                        className="stack__container__cards"
                        style={props}
                        onMouseMove={(e) => {
                            if (isDragged && ghostCard.current) {
                                const position =
                                    e.clientY -
                                    ghostCard.current.clientHeight / 2;
                                setGhostPosition({ y: `${position}px` });
                            }
                        }}
                        onMouseUp={() => {
                            handleDrop();
                            setIsDragged(false);
                            setDraggedCharacter(null);
                            setInsertIndex(null);
                        }}
                    >
                        {isDragged && draggedCharacter !== null ? (
                            <span
                                className="stack__container__cards--ghost"
                                style={{ top: ghostPosition.y }}
                                ref={ghostCard}
                            >
                                <CharacterCard
                                    character={characterList[draggedCharacter]}
                                    onClick={() => {
                                        console.log("Dragging");
                                    }}
                                />{" "}
                            </span>
                        ) : null}
                        {characterList.length > 0 &&
                            characterList
                                .filter((_c, i) => i !== draggedCharacter)
                                .map((character, index) => (
                                    <CharacterCard
                                        character={character}
                                        key={`${character.first_name}_${index} `}
                                        /*@ts-ignore */
                                        onMouseDown={(e) => {
                                            dragTimeout = setTimeout(() => {
                                                setDraggedCharacter(index);
                                                setIsDragged(true);
                                                const position =
                                                    e.clientY -
                                                    e.target.clientHeight / 2;
                                                setGhostPosition({
                                                    y: position + "px",
                                                });
                                            }, 400);
                                        }}
                                        onMouseUp={() => {
                                            clearTimeout(dragTimeout!);
                                            setDraggedCharacter(null);
                                            setIsDragged(false);
                                        }}
                                        onMouseMove={(e: MouseEvent) => {
                                            handleHover(e, index);
                                        }}
                                        onClick={() =>
                                            removeCharacterFromStack(
                                                character.version_id,
                                            )
                                        }
                                    />
                                ))}
                    </animated.div>
                </animated.div>
                <button
                    className={
                        open
                            ? "stack__button stack__button--open"
                            : "stack__button"
                    }
                    type="button"
                    title="Open the stack"
                    onClick={() => {
                        springApi.start({
                            from: { opacity: 0, height: 0 },
                            to: { opacity: 1, height: 350 },
                            reverse: open,
                        });
                        setOpen(!open);
                    }}
                >
                    {open ? (
                        <XMarkIcon />
                    ) : (
                        <span className="stack__button__text">
                            {characterList.length}
                        </span>
                    )}
                </button>
            </div>
        </>
    );
};
export default Stack;
